  import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://quizappbackend-k09m.onrender.com";

// ============================================================
// HELPERS
// ============================================================

const shuffleArray = (array) => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// ============================================================
// COMPONENT
// ============================================================

function QuizPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const student = state?.student;

  const initialExamStartTime = state?.examStartTime
    ? new Date(state.examStartTime)
    : null;
  const initialExamDuration = state?.examDuration || 30;

  // ------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------

  const [questions, setQuestions] = useState([]);
  const [quizReady, setQuizReady] = useState(false);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(initialExamDuration * 60);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quizActive, setQuizActive] = useState(false);
  const [waitTime, setWaitTime] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [isLandscape, setIsLandscape] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [startChecked, setStartChecked] = useState(false);
  const [serverStartTime, setServerStartTime] = useState(initialExamStartTime);
  const [serverEndTime, setServerEndTime] = useState(null);
  const [serverDurationMinutes, setServerDurationMinutes] = useState(initialExamDuration);

  // ------------------------------------------------------------
  // REFS
  // ------------------------------------------------------------

  const timerRef = useRef(null);
  const autoSubmitted = useRef(false);
  const statusIntervalRef = useRef(null);
  const startRequestInProgress = useRef(false);
  // Server-time countdown reference. No device clock and no offset.
  const serverCountdownRef = useRef({
    remainingSeconds: null,
    performanceBase: null,
  });

  // ------------------------------------------------------------
  // SERVER-TIME COUNTDOWN (NO CLOCK OFFSET)
  // ------------------------------------------------------------

  const syncCountdownFromServer = useCallback((serverNowMs, endMs) => {
    if (!Number.isFinite(serverNowMs) || !Number.isFinite(endMs)) {
      return null;
    }

    // Calculate the initial remaining time using ONLY timestamps supplied
    // by the backend. The device's current clock is not involved.
    const remainingSeconds = Math.max(
      0,
      Math.ceil((endMs - serverNowMs) / 1000)
    );

    serverCountdownRef.current = {
      remainingSeconds,
      performanceBase: performance.now(),
    };

    return remainingSeconds;
  }, []);

  const getServerRemainingSeconds = useCallback(() => {
    const sync = serverCountdownRef.current;

    if (sync.remainingSeconds === null || sync.performanceBase === null) {
      return null;
    }

    const elapsedSeconds = Math.floor(
      (performance.now() - sync.performanceBase) / 1000
    );

    return Math.max(0, sync.remainingSeconds - elapsedSeconds);
  }, []);

  // ------------------------------------------------------------
  // FULLSCREEN
  // ------------------------------------------------------------

  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    ) {
      return;
    }
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch((err) => console.error("Fullscreen request failed:", err));
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }, []);

  // ------------------------------------------------------------
  // REDIRECT IF NO STUDENT
  // ------------------------------------------------------------

  useEffect(() => {
    if (!student) {
      navigate("/login", { replace: true });
    }
  }, [student, navigate]);

  // ------------------------------------------------------------
  // FULLSCREEN CHANGE LISTENER
  // ------------------------------------------------------------

  useEffect(() => {
    const handleFullscreenChange = () => {
      const currentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(currentlyFullscreen);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, []);

  // ------------------------------------------------------------
  // ORIENTATION
  // ------------------------------------------------------------

  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeNow =
        window.matchMedia("(orientation: landscape)").matches ||
        (window.screen?.orientation?.type?.startsWith("landscape") ?? false) ||
        window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeNow);
    };

    checkOrientation();
    const handleChange = () => checkOrientation();
    window.addEventListener("orientationchange", handleChange);
    window.addEventListener("resize", handleChange);
    if (window.screen?.orientation) {
      window.screen.orientation.addEventListener("change", handleChange);
    }

    return () => {
      window.removeEventListener("orientationchange", handleChange);
      window.removeEventListener("resize", handleChange);
      if (window.screen?.orientation) {
        window.screen.orientation.removeEventListener("change", handleChange);
      }
    };
  }, []);

  // ============================================================
  // CHECK EXAM STATUS USING SERVER TIME ONLY
  // ============================================================

  useEffect(() => {
    if (!student || submitted) return;

    let cancelled = false;

    const checkStatus = async () => {
      try {
        const res = await fetch(`${API_BASE}/quiz-status`, { cache: "no-store" });

        if (!res.ok) {
          throw new Error(`Quiz status request failed (${res.status})`);
        }

        const data = await res.json();
        if (cancelled) return;

        // Backend is authoritative. We intentionally use serverNow only.
        const serverNow = data?.serverNow
          ? new Date(data.serverNow).getTime()
          : NaN;

        const start = data?.startTime ? new Date(data.startTime) : null;
        const end = data?.endTime ? new Date(data.endTime) : null;

        if (start && !Number.isNaN(start.getTime())) {
          setServerStartTime(start);
        }
        if (end && !Number.isNaN(end.getTime())) {
          setServerEndTime(end);
        }
        if (data.durationMinutes !== undefined && data.durationMinutes !== null) {
          setServerDurationMinutes(Number(data.durationMinutes));
        }

        if (!Number.isFinite(serverNow) || !start || !end ||
            Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          console.warn("Quiz status missing valid server timestamps.");
          return;
        }

        // Backend flags are authoritative. Never use the device clock.
        if (data?.hasEnded === true) {
          setQuizActive(false);
          setTimeLeft(0);

          if (!autoSubmitted.current && !submitting) {
            alert("The quiz has ended. You cannot take it now.");
            navigate("/login", { replace: true });
          }
          return;
        }

        if (data?.isQuizOpen === false) {
          setQuizActive(false);

          const waitSeconds = Math.max(
            0,
            Math.ceil((start.getTime() - serverNow) / 1000)
          );
          setWaitTime(waitSeconds);
          return;
        }

        // Backend says the quiz is open.
        setQuizActive(true);
        setWaitTime(null);

        // startTime/endTime are the quiz window supplied by the backend.
        // The countdown is ALWAYS: server endTime - serverNow.
        const remaining = syncCountdownFromServer(
          serverNow,
          end.getTime()
        );

        if (remaining !== null) {
          setTimeLeft(remaining);
        }
      } catch (err) {
        console.error("Quiz status check error:", err);
      }
    };

    checkStatus();
    statusIntervalRef.current = setInterval(checkStatus, 1000);

    return () => {
      cancelled = true;
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
        statusIntervalRef.current = null;
      }
    };
  }, [student, submitted, submitting, navigate, syncCountdownFromServer]);

  // ============================================================
  // START QUIZ
  // ============================================================

  useEffect(() => {
    if (!student || !quizActive || startChecked || startRequestInProgress.current) return;

    let cancelled = false;

    const startQuiz = async () => {
      startRequestInProgress.current = true;
      try {
        const res = await fetch(`${API_BASE}/start-quiz`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          cache: "no-store",
          body: JSON.stringify({ regNo: student.regNo }),
        });
        const data = await res.json();
        if (cancelled) return;

        if (res.status === 403) {
          alert(data?.message || "You have already submitted this quiz. You cannot start again.");
          navigate("/login", { replace: true });
          return;
        }

        if (!res.ok || !data.success) {
          throw new Error(data?.message || "Could not start the quiz.");
        }

        if (data.startTime) {
          const attemptStart = new Date(data.startTime);
          if (!Number.isNaN(attemptStart.getTime())) {
            // accepted
          }
        }
        if (data.durationMinutes !== undefined && data.durationMinutes !== null) {
          setServerDurationMinutes(Number(data.durationMinutes));
        }
        setStartChecked(true);
      } catch (err) {
        console.error("Start quiz error:", err);
        if (!cancelled) {
          alert(err.message || "Could not start the quiz. Please try again.");
          navigate("/login", { replace: true });
        }
      } finally {
        startRequestInProgress.current = false;
      }
    };

    startQuiz();
    return () => { cancelled = true; };
  }, [student, quizActive, startChecked, navigate]);

  // ============================================================
  // FETCH QUESTIONS
  // ============================================================

  useEffect(() => {
    if (!quizActive || !startChecked || !student) return;

    let cancelled = false;

    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      setQuizReady(false);
      setQuestionsError(null);
      try {
        const res = await fetch(`${API_BASE}/get-questions`, { cache: "no-store" });
        const data = await res.json();
        if (!res.ok || !data.success) {
          throw new Error(data?.message || "Could not load questions.");
        }
        if (!Array.isArray(data.questions)) {
          throw new Error("Invalid questions response from server.");
        }
        const withIndex = data.questions.map((q, idx) => ({ ...q, originalIndex: idx }));
        const shuffled = shuffleArray(withIndex);
        if (cancelled) return;
        setQuestions(shuffled);
        setAnswers(new Array(shuffled.length).fill(null));
        setCurrent(0);
        setQuizReady(true);
      } catch (err) {
        console.error("Question loading error:", err);
        if (!cancelled) setQuestionsError(err.message || "Failed to load questions.");
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    };

    fetchQuestions();
    return () => { cancelled = true; };
  }, [quizActive, startChecked, student]);

  // ============================================================
  // NAVIGATION
  // ============================================================

  const goTo = useCallback((idx) => {
    if (idx >= 0 && idx < questions.length) setCurrent(idx);
  }, [questions.length]);

  // ============================================================
  // SELECT ANSWER
  // ============================================================

  const selectAnswer = useCallback((key) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[current] = key;
      return updated;
    });
  }, [current]);

  // ============================================================
  // CLEAR ANSWER
  // ============================================================

  const clearAnswer = useCallback(() => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[current] = null;
      return updated;
    });
  }, [current]);

  // ============================================================
  // SUBMIT QUIZ (MANUAL ONLY – NO AUTO)
  // ============================================================

  const submitQuiz = useCallback(async () => {
    if (submitted || submitting) return;

    const confirmed = window.confirm("Are you sure you want to submit the quiz?");
    if (!confirmed) return;

    setSubmitted(true);
    setSubmitting(true);

    // Stop timer and polling
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }

    try {
      // Convert shuffled answers back to original order
      const originalAnswers = new Array(questions.length).fill(null);
      questions.forEach((question, shuffledIndex) => {
        originalAnswers[question.originalIndex] = answers[shuffledIndex] ?? null;
      });

      const res = await fetch(`${API_BASE}/submit-quiz`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          regNo: student.regNo,
          answers: originalAnswers,
          auto: false, // always manual
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Submission failed.");

      // Exit fullscreen
      try {
        if (document.fullscreenElement && document.exitFullscreen) {
          await document.exitFullscreen();
        }
      } catch (fullscreenError) {
        console.log("Could not exit fullscreen:", fullscreenError);
      }

      // Manual submission → go to result
      navigate("/result", {
        replace: true,
        state: { ...data, student, totalQuestions: questions.length },
      });
    } catch (err) {
      console.error("Submission error:", err);
      alert("Submission failed: " + (err.message || "Unknown error"));
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [submitted, submitting, questions, answers, student, navigate]);

  // ============================================================
  // HANDLE TIMER EXPIRATION – NO AUTO-SUBMIT, JUST REDIRECT
  // ============================================================

  const handleTimeExpired = useCallback(() => {
    if (autoSubmitted.current) return;
    autoSubmitted.current = true;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (statusIntervalRef.current) {
      clearInterval(statusIntervalRef.current);
      statusIntervalRef.current = null;
    }

    // Redirect to disqualified without submitting anything
    navigate("/disqualified", {
      replace: true,
      state: {
        student,
        totalQuestions: questions.length,
        reason: "timeout",
      },
    });
  }, [navigate, student, questions.length]);

  // ============================================================
  // SERVER-TIME COUNTDOWN
  // ============================================================

  useEffect(() => {
    if (!quizActive || !startChecked || !quizReady || submitted || !questions.length) {
      return;
    }

    const updateTimer = () => {
      const remaining = getServerRemainingSeconds();
      if (remaining === null) return;

      setTimeLeft(remaining);

      if (remaining <= 0) {
        handleTimeExpired();
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 250);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    quizActive,
    startChecked,
    quizReady,
    submitted,
    questions.length,
    handleTimeExpired,
    getServerRemainingSeconds,
  ]);

  // ============================================================
  // CLEANUP
  // ============================================================

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
    };
  }, []);

  // ============================================================
  // MOBILE
  // ============================================================

  const isMobile = window.innerWidth < 768 || "ontouchstart" in window;

  // ============================================================
  // LANDSCAPE OVERLAY
  // ============================================================

  if (isMobile && !isLandscape) {
    return (
      <div style={styles.landscapeOverlay}>
        <div style={styles.landscapeContent}>
          <div style={styles.rotateIcon}>📱</div>
          <h2 style={{ color: "#000" }}>Please rotate your device</h2>
          <p style={{ color: "#000" }}>
            This quiz must be taken in <strong>landscape</strong> mode.
          </p>
          <p style={{ color: "#000", fontSize: "0.9rem" }}>Turn your phone sideways to continue.</p>
        </div>
      </div>
    );
  }

  // ============================================================
  // WAITING SCREEN
  // ============================================================

  if (!quizActive) {
    const totalSecs = Math.max(0, waitTime || 0);
    const hours = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    const startTimeStr = serverStartTime
      ? serverStartTime.toLocaleTimeString("en-IN", {
          timeZone: "Asia/Kolkata",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "soon";

    return (
      <div style={styles.waitContainer}>
        <div style={styles.overlay}>
          <div style={styles.warningCard}>
            <h2 style={{ color: "#000" }}>🔒 Quiz will begin in</h2>
            <p style={{ color: "#000" }}>
              Scheduled start at <strong>{startTimeStr}</strong> IST
            </p>
            <div style={{ ...styles.countdown, color: "#0066b3" }}>
              {hours.toString().padStart(2, "0")}:{mins.toString().padStart(2, "0")}:{secs.toString().padStart(2, "0")}
            </div>
            <p style={{ color: "#000" }}>The quiz will start automatically when the scheduled exam time is reached.</p>
            <p style={{ color: "#555", fontSize: "0.85rem" }}>Please keep this page open.</p>
          </div>
        </div>
        <div style={styles.blurredQuiz}>
          <div style={styles.fakeHeader}>
            <div style={styles.fakeLogo} />
            <div style={styles.fakeStudent}>
              <div style={styles.fakeLine} />
              <div style={{ ...styles.fakeLine, width: "70%" }} />
            </div>
            <div style={styles.fakeTimer} />
          </div>
          <div style={styles.fakeBody}>
            <div style={styles.fakeQuestionCard}>
              <div style={{ ...styles.fakeLine, width: "85%", height: 24 }} />
              <div style={{ height: 30 }} />
              {[1, 2, 3, 4].map((i) => (
                <div key={i} style={styles.fakeOption}>
                  <div style={styles.fakeRadio} />
                  <div style={{ ...styles.fakeLine, flex: 1 }} />
                </div>
              ))}
              <div style={{ height: 30 }} />
              <div style={styles.fakeButtons}>
                <div style={styles.fakeButton} />
                <div style={styles.fakeButton} />
              </div>
            </div>
            <div style={styles.fakeSidebar}>
              {Array.from({ length: 25 }).map((_, i) => (
                <div key={i} style={styles.fakePalette} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // FULLSCREEN BLOCKER
  // ============================================================

  if (quizActive && quizReady && !isFullscreen && !submitted) {
    return (
      <div style={styles.fullscreenOverlay}>
        <div style={styles.overlayCard}>
          <h2 style={{ color: "#dc2626" }}>⚠️ Fullscreen Required</h2>
          <p style={{ color: "#000", margin: "1rem 0" }}>
            You must be in fullscreen mode to take this exam. Leaving fullscreen may result in disqualification.
          </p>
          <button onClick={enterFullscreen} style={styles.primaryBtn}>
            Enter Fullscreen
          </button>
        </div>
      </div>
    );
  }

  // ============================================================
  // LOADING QUESTIONS
  // ============================================================

  if (loadingQuestions || !quizReady) {
    return (
      <div style={styles.loadingOverlay}>
        <div
          className="spinner"
          style={{
            margin: "1rem auto",
            width: 50,
            height: 50,
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #0066b3",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <h2 style={{ color: "#000" }}>Loading Questions...</h2>
        <p style={{ color: "#000" }}>Please wait</p>
      </div>
    );
  }

  // ============================================================
  // QUESTION ERROR
  // ============================================================

  if (questionsError) {
    return (
      <div style={styles.loadingOverlay}>
        <h2 style={{ color: "#dc2626" }}>⚠️ Error loading questions</h2>
        <p style={{ color: "#000" }}>{questionsError}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1.5rem",
            backgroundColor: "#0066b3",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================
  // SUBMITTING
  // ============================================================

  if (submitting) {
    return (
      <div style={styles.loadingOverlay}>
        <div
          className="spinner"
          style={{
            margin: "1rem auto",
            width: 50,
            height: 50,
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #0066b3",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <h2 style={{ color: "#000" }}>Calculating your result...</h2>
        <p style={{ color: "#000" }}>Please wait</p>
      </div>
    );
  }

  // ============================================================
  // NO QUESTIONS
  // ============================================================

  if (!questions.length) {
    return (
      <div style={styles.loadingOverlay}>
        <h2 style={{ color: "#000" }}>No questions available</h2>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "1rem",
            padding: "0.5rem 1.5rem",
            backgroundColor: "#0066b3",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // ============================================================
  // CURRENT QUESTION
  // ============================================================

  const q = questions[current];
  if (!q) {
    return (
      <div style={styles.loadingOverlay}>
        <div
          className="spinner"
          style={{
            width: 50,
            height: 50,
            border: "4px solid #e5e7eb",
            borderTop: "4px solid #0066b3",
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <h2 style={{ color: "#000" }}>Preparing Exam...</h2>
      </div>
    );
  }

  // ============================================================
  // DISPLAY DATA
  // ============================================================

  const attemptedCount = answers.filter((a) => a !== null).length;
  const totalQuestions = questions.length;
  const progress = ((current + 1) / totalQuestions) * 100;
  const custom = student?.customData || {};
  const displayName = custom.name || custom.email || student?.regNo || "Student";
  const safeTimeLeft = Number.isFinite(Number(timeLeft))
    ? Math.max(0, Math.floor(Number(timeLeft)))
    : 0;
  const timerHours = Math.floor(safeTimeLeft / 3600);
  const timerMinutes = Math.floor((safeTimeLeft % 3600) / 60);
  const timerSeconds = safeTimeLeft % 60;

  // ============================================================
  // QUIZ UI
  // ============================================================

  return (
    <div style={styles.page}>
      {/* TOP BAR */}
      <div style={styles.topBar}>
        <div style={styles.profileSection}>
          <span style={styles.profileEmoji}>👤</span>
          <div style={styles.profileDetails}>
            <strong style={{ color: "#000" }}>{displayName}</strong>
            <span style={{ fontSize: "0.8rem", color: "#000" }}>
              {student?.regNo}
              {custom.email && ` • ${custom.email}`}
            </span>
          </div>
        </div>

        {/* TIMER */}
        <div style={styles.timerSection}>
          <span style={styles.timerEmoji}>⏳</span>
          <span
            style={{
              ...styles.timerText,
              color: safeTimeLeft <= 60 ? "#dc2626" : "#000",
            }}
          >
            {timerHours.toString().padStart(2, "0")}:
            {timerMinutes.toString().padStart(2, "0")}:
            {timerSeconds.toString().padStart(2, "0")}
          </span>
        </div>

        {/* SUBMIT BUTTON (manual only) */}
        <button
          onClick={submitQuiz}
          disabled={submitted || submitting || safeTimeLeft === 0}
          style={{
            ...styles.submitBtn,
            opacity: submitted || submitting || safeTimeLeft === 0 ? 0.6 : 1,
            cursor: submitted || submitting || safeTimeLeft === 0 ? "not-allowed" : "pointer",
          }}
        >
          Submit Quiz
        </button>
      </div>

      {/* PROGRESS */}
      <div style={styles.progressBarContainer}>
        <div style={{ ...styles.progressBar, width: `${progress}%` }} />
      </div>

      {/* BODY */}
      <div style={styles.bodyRow}>
        {/* MAIN CONTENT */}
        <div style={styles.mainContent}>
          <h3 style={{ marginTop: 0, color: "#000" }}>
            Question {current + 1} of {totalQuestions}
          </h3>
          <p style={styles.questionText}>{q.question}</p>

          {q.imageUrl && (
            <img
              src={`${API_BASE}${q.imageUrl}`}
              alt="Question illustration"
              style={styles.questionImage}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}

          <div style={styles.optionsContainer}>
            {Object.entries(q.options || {}).map(([key, val]) => {
              const isSelected = answers[current] === key;
              return (
                <label
                  key={key}
                  style={{
                    ...styles.optionLabel,
                    backgroundColor: isSelected ? "#e6f0fa" : "#ffffff",
                    borderColor: isSelected ? "#0066b3" : "#d1d5db",
                  }}
                >
                  <input
                    type="radio"
                    name={`question-${current}`}
                    value={key}
                    checked={isSelected}
                    onChange={() => selectAnswer(key)}
                    style={styles.radioInput}
                  />
                  <span style={styles.radioControl}>
                    <span style={isSelected ? styles.radioDotActive : styles.radioDot} />
                  </span>
                  <span style={styles.optionText}>
                    <b style={{ color: "#000" }}>{key}.</b>{" "}
                    <span style={{ color: "#000" }}>{val}</span>
                  </span>
                </label>
              );
            })}
          </div>

          <div style={styles.navRow}>
            <button
              type="button"
              style={{
                ...styles.navBtn,
                ...(current === 0 ? styles.navBtnDisabled : {}),
              }}
              onClick={() => goTo(current - 1)}
              disabled={current === 0}
            >
              ← Previous
            </button>
            <button
              type="button"
              style={{
                ...styles.clearBtn,
                ...(answers[current] === null ? styles.clearBtnDisabled : {}),
              }}
              onClick={clearAnswer}
              disabled={answers[current] === null}
            >
              Clear Answer
            </button>
            <button
              type="button"
              style={{
                ...styles.navBtn,
                ...(current === questions.length - 1 ? styles.navBtnDisabled : {}),
              }}
              onClick={() => goTo(current + 1)}
              disabled={current === questions.length - 1}
            >
              Next →
            </button>
          </div>
        </div>

        {/* SIDEBAR */}
        <div style={styles.sidebar}>
          <h4 style={{ marginTop: 0, marginBottom: "0.5rem", color: "#000" }}>Question Palette</h4>
          <div style={styles.paletteGrid}>
            {questions.map((_, idx) => {
              const isAttempted = answers[idx] !== null;
              const isCurrent = idx === current;
              let bg, color;
              if (isAttempted) {
                bg = "#22c55e";
                color = "#fff";
              } else {
                bg = "#f3f4f6";
                color = "#000";
              }
              return (
                <div
                  key={idx}
                  onClick={() => goTo(idx)}
                  style={{
                    ...styles.paletteItem,
                    backgroundColor: bg,
                    color,
                    border: isCurrent ? "3px solid #0066b3" : "2px solid transparent",
                    cursor: "pointer",
                  }}
                >
                  {idx + 1}
                </div>
              );
            })}
          </div>
          <div style={styles.sidebarFooter}>
            <div style={{ color: "#000" }}>
              <span style={{ ...styles.dot, background: "#22c55e" }} />
              Attempted: {attemptedCount}
            </div>
            <div style={{ color: "#000" }}>
              <span style={{ ...styles.dot, background: "#f3f4f6", border: "1px solid #d1d5db" }} />
              Unattempted: {totalQuestions - attemptedCount}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// STYLES (unchanged)
// ============================================================

const styles = {
  fullscreenOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.85)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 99999,
    backdropFilter: "blur(5px)",
  },
  overlayCard: {
    backgroundColor: "#ffffff",
    padding: "clamp(1.5rem, 4vw, 2.5rem) clamp(1.5rem, 5vw, 3rem)",
    borderRadius: "16px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: 450,
    width: "90%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  landscapeOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "#f8fafc",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
  },
  landscapeContent: {
    textAlign: "center",
    padding: "2rem",
    maxWidth: 400,
  },
  rotateIcon: {
    fontSize: "4rem",
    marginBottom: "1rem",
    display: "inline-block",
    animation: "tilt 2s ease-in-out infinite",
  },
  waitContainer: {
    position: "relative",
    width: "100%",
    height: "100vh",
    overflow: "hidden",
    backgroundColor: "#f8fafc",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  warningCard: {
    backgroundColor: "#ffffff",
    padding: "2.5rem 3rem",
    borderRadius: "16px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: 450,
    width: "90%",
  },
  countdown: {
    fontSize: 48,
    fontWeight: "bold",
    margin: "15px 0",
    letterSpacing: 2,
    fontVariantNumeric: "tabular-nums",
  },
  blurredQuiz: {
    position: "absolute",
    inset: 0,
    background: "#f5f7fb",
    filter: "blur(8px)",
    transform: "scale(1.04)",
    opacity: 0.9,
    zIndex: 0,
    overflow: "hidden",
  },
  page: {
    fontFamily: "'Segoe UI', Roboto, system-ui, sans-serif",
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    backgroundColor: "#f8fafc",
  },
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0.75rem 2rem",
    backgroundColor: "#ffffff",
    borderBottom: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    flexWrap: "wrap",
    gap: "0.75rem",
  },
  profileSection: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  profileEmoji: {
    fontSize: "1.4rem",
  },
  profileDetails: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.3,
    fontSize: "0.95rem",
    color: "#000",
  },
  timerSection: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    backgroundColor: "#f1f5f9",
    padding: "0.3rem 1rem",
    borderRadius: "30px",
    border: "1px solid #e5e7eb",
  },
  timerEmoji: {
    fontSize: "1.2rem",
  },
  timerText: {
    fontSize: "1.2rem",
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
  },
  primaryBtn: {
    padding: "clamp(0.5rem, 2vw, 0.6rem) clamp(1rem, 3vw, 1.5rem)",
    fontSize: "clamp(0.9rem, 2vw, 1rem)",
    fontWeight: 600,
    backgroundColor: "#0066b3",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0, 102, 179, 0.25)",
    width: "fit-content",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.3rem 1.2rem",
    fontSize: "0.9rem",
    fontWeight: 600,
    backgroundColor: "#0066b3",
    color: "#fff",
    border: "none",
    borderRadius: "30px",
    transition: "all 0.2s",
    boxShadow: "0 2px 8px rgba(0, 102, 179, 0.25)",
    flex: "0 0 auto",
    width: "auto",
    whiteSpace: "nowrap",
  },
  progressBarContainer: {
    height: 4,
    backgroundColor: "#e5e7eb",
    width: "100%",
  },
  progressBar: {
    height: "100%",
    backgroundColor: "#0066b3",
    transition: "width 0.3s ease",
  },
  bodyRow: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    padding: "1rem",
    gap: "1rem",
  },
  mainContent: {
    flex: 1,
    padding: "1.5rem 2rem",
    overflowY: "auto",
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  questionText: {
    fontSize: "1.15rem",
    lineHeight: 1.7,
    margin: "1rem 0 0.5rem",
    color: "#000",
    fontWeight: 500,
  },
  questionImage: {
    maxWidth: "100%",
    maxHeight: "220px",
    margin: "0.75rem 0",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #e5e7eb",
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "0.5rem",
    margin: "1rem 0 1.5rem",
  },
  optionLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.4rem 0.8rem",
    borderRadius: "8px",
    border: "2px solid #d1d5db",
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontSize: "0.95rem",
    color: "#000",
    width: "auto",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  radioInput: {
    position: "absolute",
    opacity: 0,
    width: 0,
    height: 0,
    pointerEvents: "none",
  },
  radioControl: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: "2px solid #9ca3af",
    flexShrink: 0,
  },
  radioDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "transparent",
  },
  radioDotActive: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "#0066b3",
  },
  optionText: {
    flex: "0 1 auto",
    lineHeight: 1.4,
    color: "#000",
    whiteSpace: "nowrap",
  },
  navRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "0.6rem",
    marginTop: "1.5rem",
  },
  navBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.3rem 0.9rem",
    fontSize: "0.85rem",
    fontWeight: 500,
    backgroundColor: "#f1f5f9",
    color: "#000",
    border: "1px solid #d1d5db",
    borderRadius: "30px",
    cursor: "pointer",
    transition: "all 0.2s",
    flex: "0 0 auto",
    width: "auto",
    whiteSpace: "nowrap",
  },
  navBtnDisabled: {
    backgroundColor: "#e5e7eb",
    color: "#9ca3af",
    border: "1px solid #d1d5db",
    cursor: "not-allowed",
    opacity: 0.6,
    boxShadow: "none",
  },
  clearBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0.3rem 0.9rem",
    fontSize: "0.85rem",
    fontWeight: 500,
    backgroundColor: "#fef3c7",
    color: "#000",
    border: "1px solid #fcd34d",
    borderRadius: "30px",
    cursor: "pointer",
    transition: "all 0.2s",
    flex: "0 0 auto",
    width: "auto",
    whiteSpace: "nowrap",
  },
  clearBtnDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  sidebar: {
    width: 220,
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
    padding: "1.2rem 1rem",
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
  },
  paletteGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "0.5rem",
    margin: "0.5rem 0 1rem",
  },
  paletteItem: {
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
    fontWeight: 600,
    fontSize: "0.85rem",
    margin: "0 auto",
    transition: "all 0.15s ease",
  },
  sidebarFooter: {
    borderTop: "1px solid #e5e7eb",
    paddingTop: "0.75rem",
    fontSize: "0.85rem",
    display: "flex",
    flexDirection: "column",
    gap: "0.4rem",
    color: "#000",
  },
  dot: {
    display: "inline-block",
    width: 12,
    height: 12,
    borderRadius: "50%",
    marginRight: 6,
  },
  loadingOverlay: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    fontFamily: "'Segoe UI', Roboto, system-ui, sans-serif",
    backgroundColor: "#f8fafc",
  },
  fakeHeader: {
    height: 70,
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 30px",
    borderBottom: "1px solid #ddd",
  },
  fakeLogo: {
    width: 180,
    height: 28,
    borderRadius: 6,
    background: "#d8dce6",
  },
  fakeStudent: {
    width: 250,
  },
  fakeTimer: {
    width: 80,
    height: 40,
    borderRadius: 8,
    background: "#d8dce6",
  },
  fakeBody: {
    display: "flex",
    padding: 30,
    gap: 30,
  },
  fakeQuestionCard: {
    flex: 1,
    background: "#fff",
    borderRadius: 14,
    padding: 30,
    boxShadow: "0 8px 30px rgba(0,0,0,.08)",
  },
  fakeSidebar: {
    width: 240,
    background: "#fff",
    borderRadius: 14,
    padding: 20,
    display: "grid",
    gridTemplateColumns: "repeat(5,1fr)",
    gap: 12,
  },
  fakePalette: {
    width: 36,
    height: 36,
    borderRadius: 6,
    background: "#d8dce6",
  },
  fakeLine: {
    height: 18,
    background: "#d8dce6",
    borderRadius: 10,
  },
  fakeOption: {
    display: "flex",
    alignItems: "center",
    gap: 15,
    marginBottom: 18,
  },
  fakeRadio: {
    width: 20,
    height: 20,
    borderRadius: "50%",
    background: "#d8dce6",
  },
  fakeButtons: {
    display: "flex",
    justifyContent: "space-between",
  },
  fakeButton: {
    width: 120,
    height: 42,
    borderRadius: 8,
    background: "#d8dce6",
  },
};

// ============================================================
// GLOBAL ANIMATIONS
// ============================================================

if (typeof document !== "undefined" && !document.getElementById("quiz-page-global-styles")) {
  const styleSheet = document.createElement("style");
  styleSheet.id = "quiz-page-global-styles";
  styleSheet.textContent = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    @keyframes tilt {
      0% { transform: rotate(0deg); }
      50% { transform: rotate(90deg); }
      100% { transform: rotate(0deg); }
    }
  `;
  document.head.appendChild(styleSheet);
}

export default QuizPage;
